import { Injectable } from '@angular/core';
import { FormActionHandlerService } from './base.service';
import { FormStatusService } from '../status.service';
import { SwalService } from '../../../fastlane-common/services/swal.service';
import { FormDataService } from '../data.service';
import { FormEmailService } from '../email.service';
import { UserContextService } from '../../../user/services/user-context.service';
import { formTypes, formStatus } from '../../../form/form.constants';
import { User } from '../../../user/models/user.model';
import { FormShareService } from '../share.service';
import { mergeMap } from 'rxjs/operators';
import { ScheduledEmail } from '../../../email/interfaces/scheduled-email.interface';
import { EmailService } from '../../../email/services/email.service';
import { EmailParameters } from '../../../email/models/email-parameters.model';
import {
  dateTypes,
  contactType,
  incentiveProgram
} from '../../../project/project.constants';
import { AuditChange } from '../../../fastlane-common/event/interfaces/audit-change';
import { EventContext } from '../../../fastlane-common/event/interfaces/event-context.interface';
import { events } from '../../../fastlane-common/event/event.constants';
import { PublishSubscribeService } from '../../../fastlane-common/services/publish-subscribe.service';
import { AuditForm } from '../../../entertainment/interfaces/audit-form.interface';
import { AuditInfo } from '../../../entertainment/interfaces/audit-info.interface';
import { Project as FilmProject } from '../../../entertainment/models/film/project.model';
import { Project as DigitalProject } from '../../../entertainment/models/digital/project.model';
import { Audit as FilmAudit } from '../../../entertainment/models/film/audit.model';
import { setStartoftheDayTime } from '../../../project/project.functions';
import { Audit as DigitalAudit } from '../../../entertainment/models/digital/audit.model';
import { Evr  } from '../../../entertainment/models/digital/expenditure-verification.model';

@Injectable()
export class SubmitCostReportActionHandlerService extends FormActionHandlerService {
  constructor(
    private dataService: FormDataService,
    private formEmailService: FormEmailService,
    private statusService: FormStatusService,
    private shareService: FormShareService,
    private userContext: UserContextService,
    private swal: SwalService,
    private emailService: EmailService,
    private pubSubService: PublishSubscribeService
  ) {
    super();
  }

  exec(): Promise<any> {
    const that = this;
    if (that.shareService.currentIncentiveProgram === incentiveProgram.dm.code) {
      that.statusService.changeStatus(formStatus.awaitingExpenditureVerification);
    } else {
      that.statusService.changeStatus(formStatus.awaitingAudit);
    }
    // Add CostReport Received Date
    that.dataService.addAuditFormDate(
      dateTypes.costReportReceivedDate,
      that.shareService.formId,
      that.userContext.currentUser.id
    );
    // Add Form Critical Date
    if (
      that.shareService.currentIncentiveProgram === incentiveProgram.film.code
    ) {
      const audit = <FilmAudit>that.shareService.form;
      audit.auditCriticalDates.costReportReceivedDate = setStartoftheDayTime(
        new Date()
      );
    }
    if (
      that.shareService.currentIncentiveProgram === incentiveProgram.dm.code
    ) {
      const audit = <Evr>that.shareService.form;
      audit.auditCriticalDates.costReportReceivedDate = setStartoftheDayTime(
        new Date()
      );
    }

    const params = <EmailParameters>{};
    params.formId = (that.shareService.filmForm as AuditForm).sourceFormId;
    const scheduledEmail = <ScheduledEmail>{};
    scheduledEmail.emailParameters = params;
    scheduledEmail.type = 'Estimated Cost Report Submission Date';
    return new Promise((resolve, reject) => {
      that.swal.load('Submitting this form to LED for review...');
      that.dataService.saveForm().subscribe(
        successfulSave => {
          that.emailService.cancelScheduledEmails(scheduledEmail).subscribe();
          that.swal.load('Emailing stakeholders...');
          const ccList = new Array<User>();
          // Add program managers to the CC List.
          this.shareService.getProgramManager().subscribe(manager => {
            ccList.push(manager);
            // Send email to auditors, applicants and CC manager
            that.raiseAwaitingAuditEvent('');
          });
        },
        errorSaving =>
          reject(
            'We were unable to submit your form to LED for review. Please try again later.'
          )
      );
    });
  }

  raiseAwaitingAuditEvent(auditingCompanyName: string) {
    const project = this.shareService.projectBase;

    const filmProject = project as FilmProject;
    const digitalProject = project as DigitalProject;

    const form = this.shareService.auditInfo;

    // Select the one that is not null
    const entId =
      filmProject.projectInfo.filmId || digitalProject.projectInfo.digitalId;

    // Raise Event to notify of auditor change
    if (project.projectInfo.incentiveProgram === incentiveProgram.film.code) {
      this.pubSubService.raise<EventContext<AuditChange>>(
        events.auditManagementChange.code,
        formStatus.awaitingAudit,
        <EventContext<AuditChange>>{
          eventType: events.auditManagementChange.code,
          eventAction: formStatus.auditorAssigned,
          data: <AuditChange>{
            auditingCompanyName: auditingCompanyName,
            auditingCompanyId: form.auditingCompanyId,
            authorizedRepresentative: project.projectContacts.find(
              c => c.contactType === contactType.signee.code
            ),
            auditForm: form,
            contacts: project.projectContacts,
            costReportSubmissionDate:
              form.estimatedDates.costReportSubmissionDate,
            entertainmentIdType: project.projectInfo.incentiveProgram,
            entertainmentId: entId,
            incentiveProgram: project.projectInfo.incentiveProgram,
            projectGuid: project.id,
            projectId: project.projectInfo.projectId,
            projectInfoBase: project.projectInfo,
            projectName: project.projectInfo.projectName,
            season: form.season
          }
        }
      );
    } else {
      this.pubSubService.raise<EventContext<AuditChange>>(
        events.auditManagementChange.code,
        formStatus.awaitingExpenditureVerification,
        <EventContext<AuditChange>>{
          eventType: events.auditManagementChange.code,
          eventAction: formStatus.auditorAssigned,
          data: <AuditChange>{
            auditingCompanyName: auditingCompanyName,
            auditingCompanyId: form.auditingCompanyId,
            authorizedRepresentative: project.projectContacts.find(
              c => c.contactType === contactType.signee.code
            ),
            auditForm: form,
            contacts: project.projectContacts,
            entertainmentIdType: project.projectInfo.incentiveProgram,
            entertainmentId: entId,
            incentiveProgram: project.projectInfo.incentiveProgram,
            projectGuid: project.id,
            projectId: project.projectInfo.projectId,
            projectInfoBase: project.projectInfo,
            projectName: project.projectInfo.projectName
          }
        }
      );
    }
    this.shareService.refreshPage();

  }
}
